import { ChevronDown, ChevronUp, GitBranch } from 'lucide-react';
import { useState } from 'react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  category: 'kol' | 'agency' | 'brand' | 'general';
}

const faqs: FAQItem[] = [
  {
    category: 'kol',
    question: 'How do I request changes to a job invitation?',
    answer: (
      <div className="space-y-3">
        <p className="text-gray-300">If you've received an invitation but need to discuss modifications (timeline, compensation, deliverables), follow these steps:</p>
        <ol className="list-decimal list-inside space-y-2 ml-4 text-gray-300">
          <li>Go to your <strong className="text-white">Invitations</strong> page</li>
          <li>Click on the invitation you want to modify</li>
          <li>Click the <strong className="text-white">"Request Changes"</strong> button</li>
          <li>A dialog will appear asking you to describe what changes you need</li>
          <li>Type your modification request (e.g., "I need an extra week to deliver high-quality content")</li>
          <li>Click <strong className="text-white">"Send Request"</strong></li>
        </ol>
        <p className="text-sm text-gray-400 mt-3">
          Your request will be sent to the agency as a message<br />
          The invitation status will change to "Modification Requested"<br />
          The agency will review and respond to your request
        </p>
      </div>
    ),
  },
  {
    category: 'kol',
    question: 'What happens after I request modifications?',
    answer: (
      <div className="space-y-3">
        <p className="text-gray-300">The agency has three options when reviewing your modification request:</p>
        <ul className="space-y-2 text-gray-300">
          <li>
            <strong className="text-emerald-400">Approve & Accept:</strong> They accept your requested changes and the invitation is approved
          </li>
          <li>
            <strong className="text-blue-400">Revert to Pending:</strong> They send it back to pending status for further discussion
          </li>
          <li>
            <strong className="text-red-400">Reject:</strong> They decline the invitation entirely
          </li>
        </ul>
        <p className="text-sm text-gray-400 mt-3">
          You'll receive a notification when the agency responds, and you can see their response in the Messages section of the invitation.
        </p>
      </div>
    ),
  },
  {
    category: 'kol',
    question: 'Can I accept an invitation after requesting modifications?',
    answer: 'Once you request modifications, the invitation status changes to "Modification Requested" and you cannot accept it directly. You need to wait for the agency to respond to your request. If they approve your modifications, the invitation will be automatically accepted. If they revert it to pending, you can then accept, decline, or request further modifications.',
  },
  {
    category: 'agency',
    question: 'How do I handle a KOL\'s modification request?',
    answer: (
      <div className="space-y-3">
        <p className="text-gray-300">When a KOL requests modifications to an invitation, you'll see a yellow notification card:</p>
        <ol className="list-decimal list-inside space-y-2 ml-4 text-gray-300">
          <li>Go to the <strong className="text-white">Jobs</strong> page and click on the relevant job</li>
          <li>Go to the <strong className="text-white">Broadcast</strong> tab</li>
          <li>Look for invitations with status "MODIFICATION_REQUESTED"</li>
          <li>Click on the invitation to view details</li>
          <li>Read the KOL's modification request in the <strong className="text-white">Messages</strong> section</li>
          <li>Optionally, type a response message</li>
          <li>Choose one of three actions:</li>
        </ol>
        <div className="bg-white/5 p-4 rounded-lg mt-3 space-y-2 text-gray-300">
          <p><strong className="text-emerald-400">Approve & Accept:</strong> Accept the invitation with the KOL's requested changes</p>
          <p><strong className="text-blue-400">Revert to Pending:</strong> Send back to pending for the KOL to review again</p>
          <p><strong className="text-red-400">Reject:</strong> Decline the invitation entirely</p>
        </div>
      </div>
    ),
  },
  {
    category: 'agency',
    question: 'Where can I see all modification requests?',
    answer: 'Modification requests are shown in the job\'s "Broadcast" tab under "All Invitations". Invitations with the status "MODIFICATION_REQUESTED" will have a special badge. You can click on any of these invitations to view the KOL\'s request and respond accordingly.',
  },
  {
    category: 'agency',
    question: 'What happens when I approve a modification request?',
    answer: (
      <div className="space-y-2 text-gray-300">
        <p>When you click "Approve & Accept":</p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li>The invitation status changes to "ACCEPTED"</li>
          <li>The job's accepted count increases by 1</li>
          <li>Your response message (if you wrote one) is sent to the KOL</li>
          <li>The KOL can now proceed with the job</li>
          <li>An audit log entry is created for tracking</li>
        </ul>
      </div>
    ),
  },
  {
    category: 'brand',
    question: 'Can brand users handle modification requests?',
    answer: 'Modification requests are primarily handled by the agency that manages the job. Brand users (owners and admins) can view invitation statuses but the approval/rejection actions are performed by the agency team who have direct relationships with the KOLs.',
  },
  {
    category: 'general',
    question: 'What are the different invitation statuses?',
    answer: (
      <div className="space-y-2 text-gray-300">
        <ul className="space-y-2">
          <li><strong className="text-white">PENDING:</strong> Invitation sent, waiting for KOL response</li>
          <li><strong className="text-white">ACCEPTED:</strong> KOL accepted the invitation</li>
          <li><strong className="text-white">REJECTED:</strong> KOL or agency declined the invitation</li>
          <li><strong className="text-white">MODIFICATION_REQUESTED:</strong> KOL has requested changes to the terms</li>
          <li><strong className="text-white">WITHDRAWN:</strong> Invitation was withdrawn by the agency</li>
        </ul>
      </div>
    ),
  },
  {
    category: 'general',
    question: 'Can I see the conversation history about modifications?',
    answer: 'Yes! All modification requests and agency responses are stored in the invitation\'s Messages section. Both KOLs and agency users can view the complete conversation history to track all communications and decisions made about the invitation.',
  },
  {
    category: 'general',
    question: 'Are modification requests tracked in audit logs?',
    answer: 'Yes. All modification request actions (both when a KOL requests modifications and when an agency responds) are logged in the audit trail. Agency admins can view these logs in the Audit Logs section to track all activities.',
  },
];

export default function FAQ() {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'kol' | 'agency' | 'brand' | 'general'>('all');
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const filteredFaqs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const categoryColors: Record<string, string> = {
    kol: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    agency: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    brand: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    general: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-16">
        <div className="mx-auto max-w-4xl space-y-8">
          <div>
            <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-4xl font-bold text-transparent">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-400">Find answers to common questions about using kol.my</p>
            <div className="mt-4 rounded-lg border border-violet-500/30 bg-gradient-to-r from-violet-600/20 to-blue-600/20 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GitBranch className="h-6 w-6 text-violet-400" />
                  <div>
                    <p className="text-lg font-semibold text-white">Interactive System Flow Diagram</p>
                    <p className="text-sm text-gray-400">See how agency, brand, and KOL users interact</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFlowDiagram(!showFlowDiagram)}
                  className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-6 py-2 font-semibold text-white transition-colors hover:opacity-90"
                >
                  {showFlowDiagram ? 'Hide Diagram' : 'View Diagram'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-violet-600 to-blue-500 text-white'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              All Questions
            </button>
            <button
              type="button"
              onClick={() => setSelectedCategory('kol')}
              className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                selectedCategory === 'kol'
                  ? 'bg-emerald-600 text-white'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              KOL
            </button>
            <button
              type="button"
              onClick={() => setSelectedCategory('agency')}
              className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                selectedCategory === 'agency'
                  ? 'bg-blue-600 text-white'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              Agency
            </button>
            <button
              type="button"
              onClick={() => setSelectedCategory('brand')}
              className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                selectedCategory === 'brand'
                  ? 'bg-purple-600 text-white'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              Brand
            </button>
            <button
              type="button"
              onClick={() => setSelectedCategory('general')}
              className={`rounded-lg px-4 py-2 font-medium transition-colors ${
                selectedCategory === 'general'
                  ? 'bg-gray-600 text-white'
                  : 'border border-white/10 bg-white/5 text-gray-400 hover:text-white'
              }`}
            >
              General
            </button>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-4xl space-y-8">
          <div className="space-y-3">
            {filteredFaqs.map((faq, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <div
                  className="cursor-pointer hover:bg-white/5 transition-colors p-6"
                  onClick={() => toggleItem(index)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${categoryColors[faq.category]}`}>
                          {faq.category.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                    </div>
                    {openItems.has(index) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
                {openItems.has(index) && (
                  <div className="px-6 pb-6 pt-0">
                    <div className="text-gray-300 leading-relaxed border-t border-white/10 pt-4">
                      {typeof faq.answer === 'string' ? <p>{faq.answer}</p> : faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="py-12 text-center text-gray-500">
              No FAQs found for this category.
            </div>
          )}
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
}
